import { Link } from "@tanstack/react-router";
import { Alert, Badge, Card, DataTable } from "@empite/ui-components";
import type { TenantNotification } from "../admin-client";
import {
  useMarkTenantNotificationRead,
  useTenantNotifications,
} from "../admin-hooks";
import { ErrorAlert } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { when } from "./common";

/**
 * The company's billing notices — a failed renewal, a grace window about to
 * close, an expired tier — drawn on EVERY screen while any high-severity one is
 * unread.
 *
 * A notice nobody sees is a tier that lapses on a Tuesday with an inbox full of
 * warnings, so the banner lives in the layout rather than on Billing. Only
 * `high` reaches it: an info notice on every page is how operators learn to
 * stop reading the banner. The rest are on Billing's list.
 *
 * `mayRead` is the nav's own gate (`tenant_billing:read`). The list route
 * audits its refusals, so a banner that asked for a session without the grant
 * would write a denial against every page load.
 *
 * Dismissing is DURABLE and company-wide: it marks the notice read on the
 * server, so it stays dismissed after a reload and for every colleague. There
 * is no client-only hide, which would come back on the next load and teach the
 * operator the button does not work.
 */
export function BillingNoticeBanner({ mayRead }: { mayRead: boolean }) {
  const unread = useTenantNotifications(true, mayRead);
  const high = (unread.data?.notifications ?? []).filter(
    (notice) => notice.severity === "high",
  );
  if (!mayRead || high.length === 0) {
    return null;
  }

  return (
    <div className="esm-billing-notices">
      {high.map((notice) => (
        <Alert key={notice.id} tone="danger" title={notice.title}>
          <p>{notice.body}</p>
          <div className="esm-actions">
            <Link to="/billing">Open billing</Link>
            <DismissButton notice={notice} />
          </div>
        </Alert>
      ))}
    </div>
  );
}

/** A failed dismiss surfaces in the reason dialog, which stays open on error. */
function DismissButton({ notice }: { notice: TenantNotification }) {
  const markRead = useMarkTenantNotificationRead();
  return (
    <ReasonButton
      variant="ghost"
      busy={markRead.isPending}
      mutation={{
        title: "Dismiss this billing notice",
        // Reversible in the sense that matters: nothing about the bill moves,
        // and the notice stays on the Billing list.
        tone: "primary",
        description: (
          <p>
            This marks &ldquo;{notice.title}&rdquo; read for your whole company.
            It changes nothing about the bill, and the notice stays listed on
            Billing.
          </p>
        ),
        confirmLabel: "Dismiss",
        run: async (reason) => {
          await markRead.mutateAsync({ notificationID: notice.id, reason });
        },
      }}
    >
      Dismiss
    </ReasonButton>
  );
}

const severityTone = {
  high: "danger",
  warning: "warning",
  info: "neutral",
} as const;

/** Every notice, read or not — the history the banner is a window onto. */
export function BillingNoticeList() {
  const notices = useTenantNotifications(false);

  return (
    <Card title="Billing notices">
      <ErrorAlert error={notices.error} title="Could not load billing notices" />
      <DataTable<TenantNotification>
        rows={notices.data?.notifications ?? []}
        rowKey={(row) => row.id}
        empty={notices.isPending ? "Loading…" : "No billing notices."}
        columns={[
          {
            key: "severity",
            header: "Severity",
            render: (row) => (
              <Badge tone={severityTone[row.severity]}>{row.severity}</Badge>
            ),
          },
          {
            key: "title",
            header: "Notice",
            render: (row) => (
              <>
                <div>{row.title}</div>
                <span className="esm-muted-text">{row.body}</span>
              </>
            ),
          },
          {
            key: "created",
            header: "Raised",
            render: (row) => <span>{when(row.created_at)}</span>,
          },
          {
            key: "state",
            header: "",
            align: "end",
            render: (row) =>
              row.read_at ? (
                <span className="esm-muted-text">read {when(row.read_at)}</span>
              ) : (
                <DismissButton notice={row} />
              ),
          },
        ]}
      />
    </Card>
  );
}
